import React from 'react';
import { Box, Typography, Button, Stack, Container } from '@mui/material';
import {
  Assignment, CheckCircle, Security, TrendingUp, ArrowForward,
} from '@mui/icons-material';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

// ── Design tokens (match sign-in / footer) ──────────────────────────────────
const PURPLE = '#7C3AED';
const PURPLE_LIGHT = '#A78BFA';
const PURPLE_DEEP = '#6D28D9';
const INK = '#1A0F2E';
const MUTED = '#6B6480';
const BODY = '#4B4660';
const BORDER = '#ECE9F4';
const GRADIENT = 'linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)';

const FEATURES = [
  { icon: <Assignment />, title: 'Comprehensive Checklists', tag: 'CUSTOM FORMS',
    desc: 'Create detailed audit checklists with customisable sections, requirements, and per-manufacturing-facility compliance tracking.' },
  { icon: <CheckCircle />, title: 'Real-time Compliance', tag: 'LIVE TRACKING',
    desc: 'Monitor compliance status in real-time with automated calculations, expiry alerts, and live reporting.' },
  { icon: <Security />, title: 'Secure & Reliable', tag: 'ENTERPRISE SECURITY',
    desc: 'Enterprise-grade security with role-based access control, encryption at rest, and a complete audit trail.' },
  { icon: <TrendingUp />, title: 'Analytics & Insights', tag: 'SMART INSIGHTS',
    desc: 'Generate comprehensive reports and gain insights from your compliance data — track exposure and trends.' },
];

// ── Small reusable eyebrow pill ──────────────────────────────────────────────
const Eyebrow = ({ children }) => (
  <Box
    sx={{
      display: 'inline-flex', alignItems: 'center', gap: 1,
      px: 1.8, py: 0.7, borderRadius: 99, backgroundColor: '#F3EFFC',
    }}
  >
    <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: PURPLE }} />
    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.2, color: PURPLE_DEEP }}>
      {children}
    </Typography>
  </Box>
);

export default function LandingPg() {
  // If a signed-in user lands here, send them straight to their workspace.
  const { isLoggedIn, loading } = useAuth();
  if (!loading && isLoggedIn) {
    return <Navigate to="/audits" replace />;
  }

  return (
    <Box sx={{ background: '#FFFFFF', minHeight: '100%' }}>
      {/* ════ 1. HERO ════ */}
      <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 10 }, pb: { xs: 6, md: 9 }, textAlign: 'center' }}>
        <Stack alignItems="center" spacing={3}>
          <Eyebrow>COMPLIANCE AUDIT PLATFORM</Eyebrow>

          <Box>
            <Typography
              component="h1"
              sx={{
                display: 'inline', fontWeight: 800, fontSize: { xs: 44, md: 64 },
                lineHeight: 1.05, color: INK, letterSpacing: '-0.02em',
              }}
            >
              Welcome to{' '}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontWeight: 800, fontSize: { xs: 44, md: 64 }, lineHeight: 1.05, letterSpacing: '-0.02em',
                background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}
            >
              Audit.
            </Typography>
          </Box>

          <Typography sx={{ fontSize: 18, lineHeight: 1.6, color: BODY, maxWidth: 620 }}>
            Streamline your compliance audit processes with a modern platform — built for exporters,
            sustainability teams, and accredited verifiers running CBAM, energy efficiency, and the audits to come.
          </Typography>

          <Button
            component={Link}
            to="/login"
            endIcon={<ArrowForward />}
            disableElevation
            sx={{
              mt: 1, textTransform: 'none', fontWeight: 700, fontSize: 16, color: '#fff',
              px: 4, py: 1.4, borderRadius: 99, background: GRADIENT,
              boxShadow: '0 8px 24px rgba(124,58,237,0.30)',
              '&:hover': { background: 'linear-gradient(135deg, #9B7DF0 0%, #6D28D9 100%)' },
            }}
          >
            Get Started
          </Button>
        </Stack>
      </Container>

      {/* ════ 2. WHY AUDIT MANAGEMENT? ════ */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Stack alignItems="center" spacing={3}>
          <Eyebrow>WHY AUDIT MANAGEMENT?</Eyebrow>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 30, md: 42 }, lineHeight: 1.1, color: INK }}>
              Compliance ensures access.
            </Typography>
            <Typography
              sx={{
                fontWeight: 800, fontSize: { xs: 30, md: 42 }, lineHeight: 1.1,
                background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}
            >
              Audit-readiness protects margin.
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 16.5, lineHeight: 1.7, color: BODY, maxWidth: 680 }}>
            Effective compliance audit management does more than tick a regulatory box. Whether it&#39;s carbon
            border exposure, energy efficiency targets, or the next framework on the horizon — strong audit
            management reduces verifier rework, prevents costly fallbacks, and protects your margins as
            compliance requirements increase year over year.
          </Typography>
          <Typography sx={{ fontSize: 15, lineHeight: 1.6, color: MUTED, maxWidth: 620 }}>
            Audit gives every plant manager, sustainability lead, and external verifier a single source of
            truth — versioned, traceable, and ready to submit.
          </Typography>
        </Stack>
      </Container>

      {/* ════ 3. WHAT IS A COMPLIANCE AUDIT? ════ */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box
          sx={{
            background: '#FFFFFF', border: `1px solid ${BORDER}`, borderRadius: 5,
            boxShadow: '0 20px 60px rgba(124,58,237,0.08)',
            px: { xs: 3, md: 8 }, py: { xs: 5, md: 7 }, textAlign: 'center',
          }}
        >
          <Stack alignItems="center" spacing={2.5}>
            <Eyebrow>WHAT IS A COMPLIANCE AUDIT?</Eyebrow>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: 26, md: 36 }, lineHeight: 1.15, color: INK }}>
                A systematic check, review, and evaluation
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: 26, md: 36 }, lineHeight: 1.15, color: INK }}>
                of{' '}
                <Box component="span" sx={{
                  background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                }}>
                  operational data
                </Box>{' '}
                against regulatory standards.
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 15.5, lineHeight: 1.7, color: BODY, maxWidth: 720 }}>
              From CBAM declarations on goods entering the EU to BEE-certified energy audits and ISO 50001
              management systems — a compliance audit verifies the records, plans, and measurements that prove
              your operations meet the standard, kept audit-ready for accredited verifier review.
            </Typography>
          </Stack>
        </Box>
      </Container>

      {/* ════ 4. WHY CHOOSE AUDIT? — feature cards ════ */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 }, textAlign: 'center' }}>
        <Stack alignItems="center" spacing={2} sx={{ mb: 5 }}>
          <Eyebrow>PLATFORM FEATURES</Eyebrow>
          <Box>
            <Typography component="span" sx={{ fontWeight: 800, fontSize: { xs: 30, md: 42 }, color: INK }}>
              Why Choose{' '}
            </Typography>
            <Typography component="span" sx={{
              fontWeight: 800, fontSize: { xs: 30, md: 42 },
              background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              Audit?
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 16, color: BODY, maxWidth: 600 }}>
            A complete operating system for modern compliance — exporters, sustainability leads, energy
            managers, and accredited verifiers.
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 3,
            textAlign: 'left',
          }}
        >
          {FEATURES.map((f) => (
            <Box
              key={f.title}
              sx={{
                background: '#FFFFFF', border: `1px solid ${BORDER}`, borderRadius: 4,
                p: 3, transition: 'all 0.25s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 16px 40px rgba(124,58,237,0.14)',
                  borderColor: PURPLE_LIGHT,
                },
              }}
            >
              <Box
                sx={{
                  width: 48, height: 48, borderRadius: 2.5, mb: 2.5,
                  background: GRADIENT, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 6px 16px rgba(124,58,237,0.25)',
                }}
              >
                {f.icon}
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: 18, color: INK, mb: 1 }}>
                {f.title}
              </Typography>
              <Typography sx={{ fontSize: 14, lineHeight: 1.6, color: BODY, mb: 2 }}>
                {f.desc}
              </Typography>
              <Box sx={{ display: 'inline-block', px: 1.4, py: 0.5, borderRadius: 99, backgroundColor: '#F3EFFC' }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.8, color: PURPLE_DEEP }}>
                  {f.tag}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>

      {/* ════ 5. CTA BAND ════ */}
      <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 10 } }}>
        <Box
          sx={{
            background: GRADIENT, borderRadius: 5,
            px: { xs: 3, md: 7 }, py: { xs: 4, md: 5 },
            display: 'flex', flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between',
            gap: 3,
            boxShadow: '0 20px 50px rgba(124,58,237,0.30)',
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 24, md: 30 }, color: '#fff', mb: 0.5 }}>
              Ready to start your first audit?
            </Typography>
            <Typography sx={{ fontSize: 15.5, color: 'rgba(255,255,255,0.85)' }}>
              Create a workspace in under two minutes.
            </Typography>
          </Box>
          <Button
            component={Link}
            to="/login"
            endIcon={<ArrowForward />}
            disableElevation
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: 16, color: PURPLE_DEEP,
              backgroundColor: '#fff', px: 3.5, py: 1.4, borderRadius: 99, whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: '#F3EFFC' },
            }}
          >
            Start an audit
          </Button>
        </Box>
      </Container>
    </Box>
  );
}